import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';

export interface AttachOption {
  id: string;
  label: string;
  description?: string;
  badge?: { text: string; variant: BadgeVariant };
  /** Tools only — group key used for grouped rendering + "Add all". */
  group?: string;
  /** Tools only — group display label. */
  groupLabel?: string;
}

interface AttachSearchProps {
  placeholder: string;
  /** Only items NOT yet attached — parent recomputes as items are added. */
  options: AttachOption[];
  onAdd: (id: string) => void;
  /** When set, results render grouped with an "Add all N" action per group. */
  grouped?: boolean;
  onAddGroup?: (groupKey: string) => void;
}

/**
 * Always-visible search field with a results dropdown for adding resources to
 * an agent. Selecting a row adds it (and the parent drops it from `options`, so
 * the list drains as you add). Used by every section of the agent editor.
 */
export function AttachSearch({
  placeholder,
  options,
  onAdd,
  grouped,
  onAddGroup,
}: AttachSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      `${o.label} ${o.description ?? ''}`.toLowerCase().includes(q),
    );
  }, [options, query]);

  const groups = useMemo(() => {
    if (!grouped) return [];
    const map = new Map<
      string,
      { key: string; label: string; items: AttachOption[] }
    >();
    for (const o of filtered) {
      const key = o.group ?? 'other';
      if (!map.has(key))
        map.set(key, { key, label: o.groupLabel ?? key, items: [] });
      map.get(key)!.items.push(o);
    }
    return Array.from(map.values());
  }, [filtered, grouped]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleAdd = (id: string) => {
    onAdd(id);
    inputRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          className="focus-ring w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md">
          <div className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                {options.length === 0 ? 'All added' : 'No matches'}
              </p>
            ) : grouped ? (
              groups.map((g) => (
                <div key={g.key} className="mb-1 last:mb-0">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {g.label}
                    </span>
                    {onAddGroup && (
                      <button
                        type="button"
                        onClick={() => {
                          onAddGroup(g.key);
                          inputRef.current?.focus();
                        }}
                        className="text-xs font-medium text-primary-600 hover:text-primary-500"
                      >
                        Add all {g.items.length}
                      </button>
                    )}
                  </div>
                  {g.items.map((o) => (
                    <ResultRow key={o.id} option={o} onClick={() => handleAdd(o.id)} />
                  ))}
                </div>
              ))
            ) : (
              filtered.map((o) => (
                <ResultRow key={o.id} option={o} onClick={() => handleAdd(o.id)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultRow({
  option,
  onClick,
}: {
  option: AttachOption;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-accent"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {option.label}
          </span>
          {option.badge && (
            <Badge variant={option.badge.variant}>{option.badge.text}</Badge>
          )}
        </div>
        {option.description && (
          <p className="truncate text-xs text-muted-foreground">
            {option.description}
          </p>
        )}
      </div>
      <Plus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}
